<template>
    <div v-if="cards.length > 0" :class="className">
        <slider
            v-if="sliderIsActive"
            ref="slider"
            class="c-card-grid__grid"
            :options="sliderOpts"
            :slides="cards"
            :modifier="sliderModifier"
            :prevNext="prevNext"
            :fullwidth="fullwidth"
            :fullheight="true"
            :show-index="showIndex"
            :nav-horizontal-position="navHorizontalPosition"
            :is-nav-have-outline="isNavHaveOutline"
            :calc-children-height="calcChildrenHeight"
            :arrowNav="arrowNav"
        >
            <template #beforeNav>
                <div v-if="$slots.beforeNav" class="c-slider__beforeNav">
                    <slot name="beforeNav" />
                </div>
            </template>
            <template #slide="card">
                <component :is="componentName" :element="card" />
            </template>
            <template #afterNav>
                <div v-if="$slots.afterNav" class="c-slider__afterNav">
                    <slot name="afterNav" />
                </div>
            </template>
        </slider>

        <div v-else class="c-card-grid__grid" ref="grid">
            <component
                v-for="(card, i) in cards"
                :key="`card-grid-${i}`"
                :is="componentName"
                :element="card"
                :data-index="i % totalCardsByRow"
                :style="`--index-by-row: ${i % totalCardsByRow};`"
                :first-revealed="firstRevealed"
            >
                <template #asset>
                    <slot name="cardAsset" v-bind="card" />
                </template>
                <template #content>
                    <slot name="cardContent" v-bind="card" />
                </template>
                <template #link>
                    <slot name="cardLink" v-bind="card" />
                </template>
            </component>
        </div>
    </div>
</template>

<script>
import Slider from "components/Slider";
import Card from "components/Card";
import HomeProgramsCard from "@/templates/components/HomePrograms/HomeProgramsCard/HomeProgramsCard";

export default {
    name: "CardGrid",
    components: {
        Slider,
        Card,
        CardHomeProgramsCard: HomeProgramsCard
    },
    props: {
        showIndex: Boolean,
        cardType: String,
        cards: {
            type: Array,
            default: () => []
        },
        calcChildrenHeight: {
            type: Boolean,
            default: false
        },
        slider: {
            type: Boolean,
            default: false
        },
        sliderModifier: {
            type: String,
            default: ""
        },
        prevNext: {
            type: Boolean,
            default: true
        },
        fullwidth: {
            type: Boolean,
            default: false
        },
        navHorizontalPosition: {
            type: String,
            required: false,
            default: "left"
        },
        isNavHaveOutline: {
            type: Boolean,
            required: false,
            default: true
        },
        arrowNav: {
            type: String,
            required: false,
            default: "arrow-right"
        }
    },
    data: () => ({
        sliderIsActive: false,
        sliderOpts: {
            watchCSS: true,
            groupCells: true,
            percentPosition: false
        },
        totalCardsByRow: 0,
        firstRevealed: false
    }),
    mounted() {
        if (this.slider) {
            this.setSlider();

            window.addEventListener("resizeEnd", this.setSlider);
        } else {
            this.getCardsOnSameRow();
            window.addEventListener("resizeEnd", this.getCardsOnSameRow);
        }

        setTimeout(() => (this.firstRevealed = true), 3000);
    },
    computed: {
        componentName() {
            return this.cardType ? `card-${this.cardType}` : `card`;
        },
        className() {
            let classname = "c-card-grid";

            classname += this.sliderIsActive ? " -slider" : " -no-slider";

            classname += ` -${this.cardType}`;

            return classname;
        }
    },
    methods: {
        setSlider() {
            this.sliderIsActive = false;
            this.cellsHeight = false;

            this.$nextTick(() => {
                if (this.$refs.grid) {
                    const gridRows = window
                        .getComputedStyle(this.$refs.grid)
                        .getPropertyValue("grid-template-rows")
                        .split(" ").length;

                    if (gridRows > 1) {
                        this.sliderIsActive = true;
                    }
                }
            });
        },
        getCardsOnSameRow() {
            const cards = this.$el.querySelectorAll(".c-card"),
                grid = this.$refs.grid,
                totalRows = window
                    .getComputedStyle(grid)
                    .getPropertyValue("grid-template-rows")
                    .split(" ").length;

            this.totalCardsByRow = Math.ceil(cards.length / totalRows);
        }
    },
    beforeDestroy() {
        if (this.slider) {
            window.removeEventListener("resizeEnd", this.setSlider);
        } else window.removeEventListener("resizeEnd", this.getCardsOnSameRow);
    }
};
</script>

<style lang="scss">
.c-card-grid {
    $self: &;
    --grid-auto-size: auto-fill;
    --grid-min-width: 18em;
    --grid-custom-gap: var(--grid-gutter);

    &.-solution {
        --grid-auto-size: max-content;
    }

    &.-no-slider {
        .c-card-grid__grid {
            display: grid;
            grid-gap: var(--grid-custom-gap);
            grid-template-columns: repeat(var(--grid-auto-size), minmax(var(--grid-min-width), 1fr));
            justify-content: center;
            overflow-anchor: none;
        }
    }

    &.-slider {
        .c-slider {
            --slide-width: 18em;
        }

        &.-solution {
            .c-slider {
                --slide-width: calc(100%);
                --nav-bg: #{$color-light};

                @media #{md("sm")} {
                    --slide-width: calc(3 / 4 * 100%);
                }

                @media #{md("md")} {
                    --slide-width: calc(11 / 12 * 100%);
                }

                @media #{md("lg")} {
                    --slide-width: calc(3 / 4 * 100%);
                }

                @media #{md("xl")} {
                    --slide-width: 62rem;
                }
            }

            .c-btn.-underline {
                --btn-hover-bg-color: #{$color-light};
            }

            @media #{md("sm", "max")} {
                .c-slider__side {
                    flex-direction: column;
                    align-items: flex-start;

                    .c-slider__afterNav {
                        position: relative;
                        padding-top: var(--grid-gutter);
                    }
                    .c-slide__count {
                        display: none;
                    }

                    .c-slider__nav {
                        position: absolute;
                        bottom: 0;
                        overflow: visible;
                        left: 0;
                        right: 0;
                        transform: translateY(300px);
                        z-index: 1;
                        pointer-events: none;

                        .-prev {
                            border: 0;
                            pointer-events: auto;
                            transform: translateX(calc(var(--grid-gutter-half) / 2 * -1));
                        }
                        .-next {
                            border: 0;
                            transform: translateX(calc(var(--grid-gutter-half) / 2));
                            position: absolute;
                            right: 0;
                            pointer-events: auto;
                        }
                    }
                }
            }
        }

        .c-slider__inner:after {
            content: "flickity";
            display: none;
        }

        .c-card {
            width: 100%;

            &:not(:first-child) {
                margin-left: 1em;
            }
        }
    }

    @media #{md("sm")} {
        --grid-min-width: 35rem;
        --grid-custom-gap: var(--grid-gutter-half);
        &.-no-slider {
            .c-card-grid__grid {
                grid-row-gap: vh(7);
            }
        }
    }
}
</style>
